import React, { useCallback, useEffect, useRef, useState } from 'react';
import Filter from '../../reusable/Client/Filter';
import AddNewClient from '../../reusable/Client/AddNewClient';
import AssignedDriver from '../../reusable/Client/AssignedDriver';
import ManageTask from '../../reusable/Client/ManageTask';
import WorkPerformanceGraph from './Graph/ClientGraph';
import ClientListTable from './ClientListTable';
import axiosInstance from 'config/axiosInstance';
import { fetchAll } from 'config/fetchAll';
import { ClientSelection, isTargetClient } from './Utility/selection';
import { summarizeUrl } from './Utility/summary';
import DeleteClientDialog from '../../reusable/Client/dialog/DeleteClientDialog';
import { useTranslation } from 'react-i18next';
import { useSaveError } from 'reusable/useSaveError';

export type { ClientSelection };

interface ClientManagementProps {
  dateRange: { start: string; end: string };
  onClientHourChange: (value: boolean) => void;
  onSelectionChange?: (selection: ClientSelection) => void;
}

interface DeleteClientData {
  id: string;
  clientName: string;
  assign_drivers: string;
  tasks: string;
}

// Helper function to convert time strings to seconds
const timeStringToSeconds = (time: string | null): number => {
  if (!time) return 0;
  // Handle case for "days hours:minutes:seconds" and "hours:minutes:seconds"
  const daysMatch = time.match(/^(\d+)\s(\d{2}):(\d{2}):(\d{2})/);
  if (daysMatch) {
    const [_, days, hours, minutes, seconds] = daysMatch.map(Number);
    return days * 86400 + hours * 3600 + minutes * 60 + seconds;
  }

  const parts = time.split(':').map(Number);
  if (parts.length === 3) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2]; // hours:minutes:seconds
  }

  return 0;
};

// Helper function to convert seconds back to "days hh:mm:ss" format
const secondsToDaysHHMMSS = (totalSeconds: number): string => {
  const days = Math.floor(totalSeconds / 86400); // Calculate days
  totalSeconds %= 86400;

  const hours = Math.floor(totalSeconds / 3600); // Calculate hours
  totalSeconds %= 3600;

  const minutes = Math.floor(totalSeconds / 60); // Calculate minutes
  const seconds = totalSeconds % 60; // Remaining seconds

  // Format the time with days and ensure hours, minutes, and seconds have two digits
  return `${days} ${String(hours).padStart(2, '0')}:${String(minutes).padStart(
    2,
    '0'
  )}:${seconds.toFixed(6).padStart(9, '0')}`;
};

/** 409 order_conflict（openapi-schema.yaml の OrderConflict）か。 */
const isOrderConflict = (error: unknown): boolean => {
  const response = (error as { response?: { status?: number; data?: { code?: unknown } } })
    ?.response;
  return response?.status === 409 && response.data?.code === 'order_conflict';
};

const ClientManagement: React.FC<ClientManagementProps> = ({
  dateRange,
  onClientHourChange,
  onSelectionChange,
}) => {
  const { t } = useTranslation('client');
  const { showSaveError, showMessages, saveErrorPopup } = useSaveError();
  const [client, setClient] = useState<any[]>([]);
  // 読み込んだ会社の配送先の並び全体（絞り込み前、一覧と同じ並び順）の ID。
  const [allClientIds, setAllClientIds] = useState<number[]>([]);
  const [clientHour, setClientHour] = useState<any[]>([]);
  const [clientNoHour, setClientNoHour] = useState<any[]>([]);
  const [clientNoTask, setClientNoTask] = useState<any[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [showDeleteClientDialog, setShowDeleteClientDialog] =
    useState<boolean>(false);
  const [deleteClient, setDeleteClient] = useState<DeleteClientData | null>(
    null
  );
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);
  const [selectedClientNames, setSelectedClientNames] = useState<string[]>([]);
  const [showClientHour, setShowClientHour] = useState(true);

  const handleShowClientHourChange = (value: boolean) => {
    setShowClientHour(value);
    onClientHourChange(value);
  };

  // 読み込みの順番。後から始めた読み込みがあれば、前の読み込みの応答は捨てる
  // （期間や絞り込みを変えた後に、遅れた古い応答が一覧を上書きしないため）。
  const fetchSeq = useRef(0);
  // 一覧の状態（PR #99 レビュー）。並べ替えができるのは、最新の読み込みが成功して
  // サーバーの一覧が入っている 'ready' のときだけ。読み込み中（並べ替えの送信中・
  // 読み直し中・期間や絞り込みの読み込み中）は画面の order が確定しておらず、
  // 失敗したときはサーバーと食い違い得る。古い expected_order を送らないため止める。
  // ドラッグの判定は同じ描画の中でも最新を見たいので、ref にも同じ値を持つ。
  const [listStatus, setListStatusState] = useState<'loading' | 'ready' | 'failed'>('loading');
  const listStatusRef = useRef<'loading' | 'ready' | 'failed'>('loading');
  // 並べ替えの送信中。その間に絞り込みの読み込みが終わっても 'ready' にしない
  // （送信中の並べ替えと次の並べ替えが同時に走らないように。PR #99 レビュー）。
  const reorderPending = useRef(false);
  const setListStatus = useCallback((status: 'loading' | 'ready' | 'failed') => {
    const next = status === 'ready' && reorderPending.current ? 'loading' : status;
    listStatusRef.current = next;
    setListStatusState(next);
  }, []);

  // 配送先の一覧を読み込む。結果は 'loaded'（一覧が入った）、'failed'（読めなかった）、
  // 'superseded'（後から始めた読み込みに追い越された。状態はその読み込みが決める）。
  const fetchData = useCallback(async (): Promise<'loaded' | 'failed' | 'superseded'> => {
    if (!dateRange.start || !dateRange.end) return 'failed';

    const seq = ++fetchSeq.current;
    const superseded = () => seq !== fetchSeq.current;
    setListStatus('loading');
    let listLoaded = false;
    try {
      // ADM-09。1ページ目（10件）だけでなく全件を読む。
      // PR #99 レビュー。並べ替えの expected_ids は会社の配送先の並び全体と比べるため、
      // 配送先の絞り込みでもサーバーでは絞らずに全件を読み、表示だけを絞る
      // （isTargetClient が配送先の選択も見る）。
      const allClients = await fetchAll<any>('/v1/client_deliveries/');
      if (superseded()) return 'superseded';
      const selection = { taskId: selectedTaskId, clientIds: selectedClientIds };
      // ADM-12。作業を選んだら、その作業が割り当てられた配送先だけを一覧・グラフに出す。
      const targets = allClients.filter((row) => isTargetClient(row, selection));
      const targetIds = new Set(targets.map((row) => String(row.id)));
      setClient(targets);
      setAllClientIds(allClients.map((row) => row.id));
      setListStatus('ready');
      listLoaded = true;

      // ADM-12 / ADM-21。作業は task_ids、配送先は id__in。CSV と同じ URL を使う。
      const summarySelection = { taskId: selectedTaskId, clientIds: selectedClientIds };
      const responseHourUrl = summarizeUrl('with_tasks', summarySelection, dateRange);
      const responseHour = await axiosInstance.get(responseHourUrl);
      if (superseded()) return 'superseded';
      const hourRows = responseHour.data.filter((row: any) => targetIds.has(String(row.id)));
      setClientHour(hourRows);

      // Sum up the hour_spent for each client and include id, name, order, and formatted time
      const clientTaskSums = hourRows.map((client: any) => {
        const totalSeconds = client.summarize.reduce(
          (sum: number, task: any) => {
            return sum + timeStringToSeconds(task.hour_spent);
          },
          0
        );

        return {
          id: client.id,
          name: client.name,
          order: client.order,
          hour_spent: secondsToDaysHHMMSS(totalSeconds),
        };
      });

      setClientNoTask(clientTaskSums);

      const responseNoHourUrl = summarizeUrl('without_tasks', summarySelection, dateRange);
      const responseNoHour = await axiosInstance.get(responseNoHourUrl);
      if (superseded()) return 'superseded';
      setClientNoHour(
        responseNoHour.data.filter((row: any) => targetIds.has(String(row.id)))
      );
    } catch (error) {
      console.error('Failed to fetch client data', error);
      if (superseded()) return 'superseded';
      // 最新の読み込みで一覧を読めなかった。並べ替えの後かどうか（最初の読み込みを
      // 含む）に関係なく、画面の order はサーバーと食い違い得るので並べ替えを止める。
      // 一覧は読めて集計だけが失敗したときは、並びはサーバーと合っている。
      if (!listLoaded) setListStatus('failed');
    }
    return listLoaded ? 'loaded' : 'failed';
  }, [dateRange, selectedClientIds, selectedTaskId, setListStatus]);

  // PR #99 レビュー。絞り込み（期間・作業・配送先）の世代と、最新の絞り込みの fetchData。
  // 並べ替えの送信を待つ間に絞り込みが変わっても、保存後の読み直しは最新の絞り込みで
  // 行う。古い描画の fetchData で読み直すと、後から番号を得て新しい一覧と集計を
  // 前の絞り込みで上書きしていた。
  const filterGeneration = useRef(0);
  const latestFetchData = useRef(fetchData);
  latestFetchData.current = fetchData;

  useEffect(() => {
    filterGeneration.current += 1;
    fetchData();
  }, [fetchData]);

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragEnter = (index: number) => {
    if (draggedIndex === null || draggedIndex === index) return;

    setHoveredIndex(index);
  };

  const handleDragEnd = async () => {
    if (
      listStatusRef.current === 'ready' &&
      draggedIndex !== null &&
      hoveredIndex !== null &&
      draggedIndex !== hoveredIndex
    ) {
      // ADM-14。サーバーの change_order は「1件を new_order へ移動し、間を ±1 ずらす」。
      // 2件の order を入れ替えて2本同時に送ると、離れた2件では入れ替えにならず、
      // 同時実行で順番が重複・欠番になり得た。移動は1本だけ送る。
      const draggedItem = client[draggedIndex];
      const newOrder = client[hoveredIndex].order;

      // 画面もサーバーと同じ「移動」で並べる（order は応答の取り直しで確定させる）。
      const updatedClient = [...client];
      updatedClient.splice(draggedIndex, 1);
      updatedClient.splice(hoveredIndex, 0, draggedItem);
      setClient(updatedClient);
      setDraggedIndex(null);
      setHoveredIndex(null);

      // 前の移動が確定し、一覧を読み直すまで次の移動は受け付けない
      // （画面の order は確定しておらず、同時実行で順番が崩れるため）。
      reorderPending.current = true;
      setListStatus('loading');
      const generation = filterGeneration.current;
      let conflict = false;
      let saveFailed = false;
      try {
        // T02 ステップ2。画面が表示していた移動前の順番を expected_order で送る。
        // PR #99 レビュー。加えて、読み込んだ並び全体（expected_ids）を送る。サーバーは
        // expected_ids を優先し、他の管理者が別の行だけを並べ替えた場合も 409
        // order_conflict を返す（expected_order だけでは動かす行しか比べない）。
        await axiosInstance.patch(
          `/v1/client_deliveries/${draggedItem.id}/change_order/`,
          {
            new_order: newOrder,
            expected_order: draggedItem.order,
            expected_ids: allClientIds,
          }
        );
      } catch (error) {
        console.error('Failed to update client order on the server', error);
        saveFailed = true;
        conflict = isOrderConflict(error);
        if (!conflict) showSaveError(error);
      }
      // 成功・失敗どちらでも、サーバーで確定した順番を最新の絞り込みで取り直す。
      // 並べ替えを再び受け付けるかは fetchData が一覧の状態で決める。
      reorderPending.current = false;
      const reloaded = await latestFetchData.current();
      if (saveFailed && reloaded === 'failed' && generation === filterGeneration.current) {
        // 並べ替えは反映されていないのに、画面には移動後の並びが残る。移動前に戻す。
        // 絞り込みが変わっていたら、この closure の一覧は前の絞り込みのものなので戻さない。
        setClient(client);
      }
      if (conflict) {
        // 競合は入力の誤りではないので、画面の言語で知らせる。
        // 「読み直した」と言えるのは、読み直しが成功したときだけ（PR #99 レビュー）。
        showMessages([
          reloaded === 'loaded'
            ? t('ui.order-conflict')
            : reloaded === 'failed'
            ? t('ui.order-conflict-reload-failed')
            : t('ui.order-conflict-reloading'),
        ]);
      }
      return;
    }
    setDraggedIndex(null);
    setHoveredIndex(null);
  };

  const handleDeleteClick = (client: any) => {
    setDeleteClient({
      id: client.id,
      clientName: client.name,
      assign_drivers: client.assign_drivers.length + ' people',
      tasks: client.tasks.length.toString(),
    });
    setShowDeleteClientDialog(true);
  };

  const handleTaskSelect = (taskId: string | null, taskName: string) => {
    setSelectedTaskId(taskId);
  };

  const handleClientSelect = (clientIds: string[], clientNames: string[]) => {
    setSelectedClientIds(clientIds);
    setSelectedClientNames(clientNames);
  };

  // ADM-12。CSV にも同じ選択を渡す。
  useEffect(() => {
    onSelectionChange?.({ taskId: selectedTaskId, clientIds: selectedClientIds });
  }, [onSelectionChange, selectedTaskId, selectedClientIds]);

  return (
    <main className="flex flex-col bg-white">
      <div className="flex flex-col px-4 py-2">
        <div className="flex flex-row justify-between items-center">
          <div className="flex flex-col">
            <h4 className="text-subtitle-1 font-medium">{t('graph.title')}</h4>
          </div>
          <div className="flex flex-row items-center space-x-4">
            <Filter
              onTaskSelect={handleTaskSelect}
              onClientSelect={handleClientSelect}
            />
            <AddNewClient />
            <AssignedDriver />
            <ManageTask />
          </div>
        </div>
        <div>
          <WorkPerformanceGraph
            clientHour={clientHour}
            clientNoHour={clientNoTask}
            onShowClientHourChange={handleShowClientHourChange}
            showClientHour={showClientHour}
          />
        </div>
      </div>
      <div className="flex flex-col p-4 space-y-2">
        {listStatus === 'failed' && (
          <div role="alert" className="flex flex-row items-center gap-2 text-red-500">
            <p>{t('ui.order-reload-failed')}</p>
            <button
              type="button"
              className="rounded-md border px-4 py-2 text-primary/300"
              onClick={() => fetchData()}
            >
              {t('ui.retry', { ns: 'common' })}
            </button>
          </div>
        )}
        <div className="flex flex-row">
          <ClientListTable
            clientData={client}
            onDragStart={handleDragStart}
            onDragEnter={handleDragEnter}
            onDragEnd={handleDragEnd}
            onDeleteClick={handleDeleteClick}
          />
        </div>
      </div>
      {showDeleteClientDialog && deleteClient && (
        <DeleteClientDialog
          id={deleteClient.id}
          name={deleteClient.clientName}
          isOpen={showDeleteClientDialog}
          onClose={() => setShowDeleteClientDialog(false)}
          fields={[
            { label: t('table.client_id'), value: deleteClient.id },
            { label: t('table.client_name'), value: deleteClient.clientName },
            { label: t('table.assignees'), value: deleteClient.assign_drivers },
            { label: t('table.task_counts'), value: deleteClient.tasks },
          ]}
        />
      )}
      {saveErrorPopup}
    </main>
  );
};

export default ClientManagement;
